/**
 * RFC 4180 CSV serialisation.
 *
 * The table Export buttons used to build their CSV by joining cells with a bare
 * comma, so any value that itself contained a comma ("Brooklyn, NY") spilled
 * into extra columns. Anything that builds a CSV client-side should go through
 * `toCsv` instead of joining by hand.
 */

/** RFC 4180 uses CRLF between records. */
const ROW_SEPARATOR = '\r\n';

/**
 * Reduces a cell to the text that belongs in the file.
 *
 * Columns that render JSX have no meaningful CSV form, so they collapse to an
 * empty cell rather than "[object Object]" — such a column needs an
 * `exportFormat` to export anything useful.
 */
export const stringifyCsvValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  }
  // Multi-value cells (e.g. maintenance tags) keep every value in one column;
  // the comma is safe because the field gets quoted.
  if (Array.isArray(value)) {
    return value.map(stringifyCsvValue).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') return '';
  return String(value);
};

/**
 * Quotes a single field. A field only needs quoting when it contains the
 * delimiter, a double quote or a line break; embedded quotes are doubled.
 */
export const escapeCsvValue = (value: unknown): string => {
  const text = stringifyCsvValue(value);
  if (!/[",\r\n]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
};

/**
 * Serialises a header row plus data rows into a CSV document.
 *
 * Rows shorter than the header are not padded — the trailing cells are simply
 * absent, which every CSV reader treats as empty.
 */
export const toCsv = (headers: unknown[], rows: unknown[][]): string =>
  [headers, ...rows].map((row) => row.map(escapeCsvValue).join(',')).join(ROW_SEPARATOR);
